import type {
  BSACustomerPriorityKeyType,
  EssentialBackupMetrics,
} from 'elements/hardwareSelectorV2/batteryWizard/utility/types'
import addComponentSetToSystem from 'elements/hardwareSelectorV2/wizardUtils/addComponentSetToSystem'
import { activateComponentsIfNeeded } from './activateComponentsIfNeeded'

const clearSystemBatteries = ({ systemUuid }: { systemUuid: string }) => {
  const system = systemUuid && window.editor.objectByUuid(systemUuid)
  const components = system?.batteries() || []
  var commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
  components.forEach((component) => {
    window.editor.execute(new window.RemoveObjectCommand(component, true, false, commandUUID))
  })
}

const updateSystemBatteryControlScheme = ({
  systemUuid,
  customerPriority,
}: {
  systemUuid: string
  customerPriority?: BSACustomerPriorityKeyType
}) => {
  const system = systemUuid && window.editor.objectByUuid(systemUuid)
  // Change `self_consumption` to `solar_charged`, since it has a different value for battery control scheme
  // Use `solar_charged` as default if empty
  const batteryControlScheme = customerPriority === 'self_consumption' ? 'solar_charged' : customerPriority
  window.editor.execute(
    new window.SetValueCommand(system, 'battery_control_scheme', batteryControlScheme || 'solar_charged')
  )
}

const updateSystemBDASettings = ({
  systemUuid,
  essentialBackupMetrics,
}: {
  systemUuid: string
  essentialBackupMetrics?: EssentialBackupMetrics
}) => {
  const system = systemUuid && window.editor.objectByUuid(systemUuid)
  window.editor.execute(new window.SetValueCommand(system, 'essential_backup_metrics', essentialBackupMetrics || {}))
}

export async function loadBatteryDesignAssistantResult(batterySetIndex: number) {
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  // Find the message with the battery recommendations
  const state = window.reduxStore?.getState()
  const messages = state?.chatDrawer?.messages || []

  let batterySets: any[] = []
  let customerPriority: BSACustomerPriorityKeyType | undefined
  let essentialBackupMetrics: EssentialBackupMetrics | undefined

  for (let i = messages.length - 1; i >= 0; i--) {
    if (
      messages[i].direction === 'receive' &&
      messages[i].toolExecutionState &&
      messages[i].toolExecutionState.toolType === 'batteryDesignAssistant' &&
      messages[i].toolExecutionState.data?.batterySets
    ) {
      batterySets = messages[i].toolExecutionState.data.batterySets
      customerPriority = messages[i].toolExecutionState.data.customerPriority
      essentialBackupMetrics = messages[i].toolExecutionState.data.essentialBackupMetrics
      break
    }
  }

  if (!batterySets || batterySets.length === 0) {
    throw new Error('No battery recommendations found. Please run the battery design assistant first.')
  }

  if (batterySetIndex < 0 || batterySetIndex >= batterySets.length) {
    throw new Error(`Invalid battery set index: ${batterySetIndex}`)
  }

  const selectedBatterySet = batterySets[batterySetIndex]

  if (!selectedBatterySet.batteries || !Array.isArray(selectedBatterySet.batteries)) {
    throw new Error('Invalid battery set data. Missing batteries array.')
  }

  // Prepare components set - addComponentSetToSystem will handle activation
  const componentsSet = selectedBatterySet.batteries.map((battery: any) => ({
    ...battery,
    type: battery.type || 'battery',
  }))

  // Update battery control scheme and BDA settings first
  updateSystemBatteryControlScheme({ systemUuid: system.uuid, customerPriority })
  updateSystemBDASettings({ systemUuid: system.uuid, essentialBackupMetrics })

  // Clear existing batteries
  clearSystemBatteries({ systemUuid: system.uuid })

  await activateComponentsIfNeeded(componentsSet, 'battery')

  addComponentSetToSystem({ systemUuid: system.uuid, componentsSet })

  console.log('Battery design assistant result loaded')
}
